/**
 * Editor edit dispatch: routes dimension-chip and panel-input commits onto
 * the PURE floor-plan edit catalog (floor-plan/edits/). `st.shape` is
 * replaced, never mutated (Phase 3 of the state-management refactor).
 * Selection→target resolution and chip-key parsing live here; the edit
 * semantics and clamping live in the catalog.
 */
import {
  addFreeElement as addFreeElementPure,
  clamp,
  CustomAreaType,
  defaultFreeElement,
  duplicateFreeElement,
  EdgeName,
  FloorPlanRoom,
  FreeElementSide,
  OuterWhich,
  setAlcoveDepth,
  setAlcoveEndGap,
  setAlcoveOuterChamfer,
  setAlcovePosition,
  setAlcovesMaxDepth,
  setAlcoveStartGap,
  setAlcoveWidth,
  setCeilingHeight,
  setFreeElementBase,
  setFreeElementCornerChamfer,
  setFreeElementGap,
  setFreeElementHeight,
  setFreeElementPosition,
  setFreeElementRotation,
  setFreeElementSize,
  setRoomCornerChamfer,
  setRoomLength,
  setRoomWidth,
  setSegmentEndGap,
  setSegmentOpeningBase,
  setSegmentOpeningHeight,
  setSegmentPosition,
  setSegmentStartGap,
  setSegmentWidth,
} from '@kaiizen/kai-floor-plan';
import { chamferData } from './editorGeometry';
import type { EditorMutableState } from './editorState';
import { pruneZeroDepthAlcove } from './gestures';
import { select } from './selection';

/** Apply a chamfer chip value (one axis) through the catalog, chip-clamped. */
function applyChamferChip(st: EditorMutableState, axis: 'dx' | 'dy', value: number): FloorPlanRoom {
  const room = st.shape;
  const sel = st.selection;
  const cd = chamferData(st, sel);
  if (!cd) return room;
  const v = clamp(value, 0, axis === 'dx' ? cd.maxDx : cd.maxDy);
  const legs = { [axis]: v } as { dx?: number; dy?: number };
  if (sel.kind === 'corner') return setRoomCornerChamfer(room, sel.corner, legs);
  if (sel.kind === 'alcoveCorner') return setAlcoveOuterChamfer(room, sel.edge, sel.index, sel.which, legs);
  if (sel.kind === 'innerCorner') return setFreeElementCornerChamfer(room, sel.index, sel.which, legs);
  return room;
}

/** Free-element gap chips, keyed to the side whose distance to the interior bound is edited. */
const GAP_CHIP_SIDE: Partial<Record<string, FreeElementSide>> = {
  'gap-left': 'left',
  'gap-right': 'right',
  'gap-top': 'top',
  'gap-bottom': 'bottom',
};

/**
 * Does a measure chip open its length editor? Never on a read-only plan;
 * otherwise as the host's rule for its chips says (`isEditable`: every chip
 * in the room editor, the four gaps in the assembler). Both platforms' chips
 * read it, and a chip that does not wears the fixed look.
 */
export const isChipEditable = (key: string, readOnly: boolean, isEditable: (key: string) => boolean): boolean => !readOnly && isEditable(key);

/** Apply an edited dimension-chip value to the shape, then re-render. */
export function applyChipEdit(st: EditorMutableState, key: string, value: number, render: () => void): void {
  const room = st.shape;
  const sel = st.selection;
  const parts = key.split(':');
  const gapSide = GAP_CHIP_SIDE[key];
  let next = room;

  if (key === 'width') next = setRoomWidth(room, value);
  else if (key === 'length') next = setRoomLength(room, value);
  else if (key === 'chamferdx') next = applyChamferChip(st, 'dx', value);
  else if (key === 'chamferdy') next = applyChamferChip(st, 'dy', value);
  else if (parts[0] === 'alcove') {
    const edge = parts[1] as EdgeName, index = Number(parts[2]);
    next = parts[3] === 'width'
      ? setAlcoveWidth(room, edge, index, value)
      : setAlcoveDepth(room, edge, index, value);
  } else if (parts[0] === 'maxdepth') {
    next = setAlcovesMaxDepth(room, parts[1] as EdgeName, value);
  } else if (parts[0] === 'inner') {
    const index = Number(parts[1]);
    next = setFreeElementSize(room, index, parts[2] === 'width' ? { width: value } : { length: value });
  } else if (gapSide && sel.kind === 'inner') {
    next = setFreeElementGap(room, sel.index, gapSide, value);
  } else if (key === 'seg-width' || key === 'seg-position' || key === 'seg-from-start' || key === 'seg-from-end') {
    if ((key === 'seg-from-start' || key === 'seg-from-end') && (sel.kind === 'alcove' || sel.kind === 'alcoveWall')) {
      next = key === 'seg-from-start'
        ? setAlcoveStartGap(room, sel.edge, sel.index, value)
        : setAlcoveEndGap(room, sel.edge, sel.index, value);
    } else if (sel.kind === 'lineSeg') {
      const { ref, si } = sel;
      if (key === 'seg-width') next = setSegmentWidth(room, ref, si, value);
      else if (key === 'seg-position') next = setSegmentPosition(room, ref, si, value);
      else if (key === 'seg-from-start') next = setSegmentStartGap(room, ref, si, value);
      else next = setSegmentEndGap(room, ref, si, value);
    }
  }

  st.shape = next;
  pruneZeroDepthAlcove(st); // depth edited to 0 = delete (undo restores)
  render();
}

/** Apply a panel number-input value to the shape, then re-render. */
export function applyPanelInput(st: EditorMutableState, key: string, v: number, render: () => void): void {
  const room = st.shape;
  const sel = st.selection;
  let next = room;

  if (key === 'width') next = setRoomWidth(room, v);
  else if (key === 'length') next = setRoomLength(room, v);
  else if (key === 'ceiling') next = setCeilingHeight(room, v);
  else if (key === 'corner-dx' && sel.kind === 'corner') next = setRoomCornerChamfer(room, sel.corner, { dx: v });
  else if (key === 'corner-dy' && sel.kind === 'corner') next = setRoomCornerChamfer(room, sel.corner, { dy: v });
  else if (sel.kind === 'alcove' || sel.kind === 'alcoveCorner') {
    const { edge, index } = sel;
    const alcKey = /^alc-(backLeftCorner|backRightCorner)-(dx|dy)$/.exec(key);
    if (key === 'al-position') next = setAlcovePosition(room, edge, index, v);
    else if (key === 'al-width') next = setAlcoveWidth(room, edge, index, v);
    else if (key === 'al-depth') next = setAlcoveDepth(room, edge, index, v);
    else if (key === 'opening-height') next = setSegmentOpeningHeight(room, { k: 'pe', edge }, index, v);
    else if (key === 'opening-base') next = setSegmentOpeningBase(room, { k: 'pe', edge }, index, v);
    else if (alcKey) {
      next = setAlcoveOuterChamfer(room, edge, index, alcKey[1] as OuterWhich, { [alcKey[2] as 'dx' | 'dy']: Math.max(0, v) });
    }
  } else if (sel.kind === 'lineSeg') {
    const { ref, si } = sel;
    if (key === 'opening-height') next = setSegmentOpeningHeight(room, ref, si, v);
    else if (key === 'opening-base') next = setSegmentOpeningBase(room, ref, si, v);
    else if (key === 'seg-width') next = setSegmentWidth(room, ref, si, v);
    else if (key === 'seg-position') next = setSegmentPosition(room, ref, si, v);
  } else if ((sel.kind === 'inner' || sel.kind === 'innerCorner') && key.startsWith('inner-')) {
    const i = sel.index;
    if (key === 'inner-x') next = setFreeElementPosition(room, i, { x: v });
    else if (key === 'inner-y') next = setFreeElementPosition(room, i, { y: v });
    else if (key === 'inner-width') next = setFreeElementSize(room, i, { width: v });
    else if (key === 'inner-height') next = setFreeElementSize(room, i, { length: v });
    else if (key === 'inner-itemheight') next = setFreeElementHeight(room, i, v);
    else if (key === 'inner-base') next = setFreeElementBase(room, i, v);
    else if (key === 'inner-rotation') next = setFreeElementRotation(room, i, v);
  }

  st.shape = next;
  pruneZeroDepthAlcove(st); // depth edited to 0 = delete (undo restores)
  render();
}

/**
 * Add a free element of the given kind with that kind's defaults, and select
 * it. A counter is a 24 in square and anything else a square a third of the
 * room, centered; flooring gets the next unused floor type; objects default
 * to small furniture.
 * Whatever is selected has no say: copying is `duplicateSelectedFreeElement`.
 */
export function addFreeElement(st: EditorMutableState, kind: CustomAreaType): void {
  const el = defaultFreeElement(st.shape, kind, st.floorTypeOptions.map(o => o.key));
  st.shape = addFreeElementPure(st.shape, el);
  select(st, { kind: 'inner', index: (st.shape.freeElements?.length ?? 1) - 1 });
}

/**
 * Duplicate the selected free element (any kind): a complete copy of its
 * properties, offset 4 in right and down, added and selected. Nothing
 * selected, or a selection that isn't a free element: no-op.
 */
export function duplicateSelectedFreeElement(st: EditorMutableState): void {
  const sel = st.selection;
  const source = sel.kind === 'inner' || sel.kind === 'innerCorner' ? st.shape.freeElements?.[sel.index] : undefined;
  if (!source) return;
  st.shape = addFreeElementPure(st.shape, duplicateFreeElement(st.shape, source));
  select(st, { kind: 'inner', index: (st.shape.freeElements?.length ?? 1) - 1 });
}
